/* eslint-disable complexity */
import React, { useCallback, useEffect, useState } from 'react';
import { get } from 'lodash';

import { CustomPopover } from '@futrli/ui-components';

import * as styles from './filter.scss';
import { checkIfPassesFilters, isFilterClearEnabled, isFilterSubmitDisabled } from '../filter-table-helper';
import { FILTER_TYPE, Option, SORT, TableFilters, TABLE_COLUMN, Data, SubmitFilterArgs } from '../filter-table-types';
import {
  DateFilterContent,
  NumericalFilterContent,
  SelectFilterContent,
  TextFilterContent,
  TimeFilterContent
} from './filter-content';
import { SortLabel } from './sort-label';
import { FilterActions } from './filter-actions';
import { getDateWithEndOfDay, getDateWithStartOfDay } from '../../dates';

interface Props {
  data?: Data[];
  anchorElement: HTMLElement | null;
  options?: Option[];
  handleSubmit: ({ fromValue, toValue, selectedValues }: SubmitFilterArgs) => void;
  handleClose: () => void;
  handleClearColumnFilter: () => void;
  activeColumn?: TABLE_COLUMN;
  filters: TableFilters;
  sortByColumn?: TABLE_COLUMN;
  handleSortClick: (newSort: SORT) => void;
  sort?: SORT;
  minQueryLength?: number;
}

const MIN_QUERY_LENGTH = 1;

export const Filter = ({
  data,
  anchorElement,
  options,
  handleSubmit,
  handleClose,
  handleClearColumnFilter,
  activeColumn,
  filters,
  sortByColumn,
  handleSortClick,
  sort,
  minQueryLength = MIN_QUERY_LENGTH
}: Props) => {
  // for numerical filter type
  const [fromValue, setFromValue] = useState('');
  // for numerical filter type
  const [toValue, setToValue] = useState('');

  // for select filter type
  const [selectedValues, setSelectedValues] = useState<Record<string, boolean> | undefined>();

  // for text filter type
  const [filteredDisplayedData, setFilteredDisplayedData] = useState(data ? [...data] : undefined);
  const [checkedFilteredData, setCheckedFilteredData] = useState<Record<string, boolean> | undefined>();
  const [searchQuery, setSearchQuery] = useState<string | undefined>();

  // for date filter type
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();

  // for time filter type
  const [fromTime, setFromTime] = useState<string>();
  const [toTime, setToTime] = useState<string>();

  useEffect(() => {
    if (checkedFilteredData && data) {
      const checkedDataIds = Object.keys(checkedFilteredData);
      const newFilteredData: Data[] = [];
      for (const id of checkedDataIds) {
        newFilteredData.push(data.find(d => d.id === id)!);
      }
      setFilteredDisplayedData([...newFilteredData]);
    }
  }, [checkedFilteredData, data]);

  useEffect(() => {
    if (activeColumn) {
      if (filters[activeColumn].filterType === FILTER_TYPE.NUMBER) {
        const newFromValue = get(filters, `${activeColumn}.fromValue`);
        const newToValue = get(filters, `${activeColumn}.toValue`);
        setFromValue(typeof newFromValue === 'number' ? String(newFromValue) : '');
        setToValue(typeof newToValue === 'number' ? String(newToValue) : '');
      } else if (filters[activeColumn].filterType === FILTER_TYPE.DATE) {
        const newFromDate = get(filters, `${activeColumn}.fromDate`);
        const newToDate = get(filters, `${activeColumn}.toDate`);
        setFromDate(newFromDate);
        setToDate(newToDate);
      } else if (filters[activeColumn].filterType === FILTER_TYPE.TIME) {
        const newFromTime = get(filters, `${activeColumn}.fromTime`);
        const newToTime = get(filters, `${activeColumn}.toTime`);
        setFromTime(newFromTime);
        setToTime(newToTime);
      } else if (filters[activeColumn].filterType === FILTER_TYPE.SELECT) {
        const currentSelectedValues = get(filters, `${activeColumn}.selectedValues`);
        if (currentSelectedValues) {
          setSelectedValues(currentSelectedValues);
        } else {
          setSelectedValues(
            options
              ? options.reduce(
                  (acc: Record<string, boolean>, curr: Option) => ({ ...acc, [curr.id]: true }),
                  {} as Record<string, boolean>
                )
              : undefined
          );
        }
      } else if (filters[activeColumn].filterType === FILTER_TYPE.TEXT) {
        const newSearchQuery = get(filters, `${activeColumn}.searchQuery`);
        const newCheckedFilteredData = (get(filters, `${activeColumn}.checkedFilteredData`) as unknown) as Record<
          string,
          boolean
        >;
        setCheckedFilteredData(newCheckedFilteredData ? { ...newCheckedFilteredData } : undefined);
        setSearchQuery(newSearchQuery);
      }
    }
  }, [activeColumn, filters, anchorElement, options]);

  const handleSelectAll = useCallback(() => {
    const newCheckedFilteredData: Record<string, boolean> = {};
    if (checkedFilteredData && searchQuery && searchQuery.length >= minQueryLength) {
      const selectedIds = Object.keys(checkedFilteredData);
      for (const id of selectedIds) {
        newCheckedFilteredData[id] = true;
      }
      setCheckedFilteredData(newCheckedFilteredData);
    }
  }, [checkedFilteredData, searchQuery, minQueryLength]);

  const handleUnSelectAll = useCallback(() => {
    const newCheckedFilteredData: Record<string, boolean> = {};
    if (checkedFilteredData) {
      const selectedIds = Object.keys(checkedFilteredData);
      for (const id of selectedIds) {
        newCheckedFilteredData[id] = false;
      }
      setCheckedFilteredData(newCheckedFilteredData);
    }
  }, [checkedFilteredData]);

  const handleSearchQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const newSearchQuery = event.target.value;
      setSearchQuery(newSearchQuery);

      if (data && newSearchQuery.length >= minQueryLength && activeColumn) {
        const newFilters = {
          ...filters,
          [activeColumn]: {
            ...filters[activeColumn],
            searchQuery: newSearchQuery
          }
        };

        const newData = data.filter(d => checkIfPassesFilters(d, newFilters));
        const newCheckedFilteredData: Record<string, boolean> = {};
        for (const filteredData of newData) {
          newCheckedFilteredData[filteredData.id] =
            checkedFilteredData && typeof checkedFilteredData[filteredData.id] !== 'undefined'
              ? checkedFilteredData[filteredData.id]
              : false;
        }
        setFilteredDisplayedData([...newData]);
        setCheckedFilteredData({ ...newCheckedFilteredData });
      }
    },
    [data, activeColumn, filters, checkedFilteredData, minQueryLength]
  );

  const handleDataCheckChange = useCallback(
    (data: Data) => {
      if (checkedFilteredData) {
        setCheckedFilteredData({ ...checkedFilteredData, [data.id]: !checkedFilteredData[data.id] });
      }
    },
    [checkedFilteredData]
  );

  const handleFromValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setFromValue(event.target.value),
    []
  );

  const handleToValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setToValue(event.target.value),
    []
  );

  const handleSelectValuesChange = useCallback(
    (id: string) => {
      if (selectedValues) {
        setSelectedValues({ ...selectedValues, [id]: !selectedValues[id] });
      }
    },
    [selectedValues]
  );

  const handleFromDateChange = useCallback((date: Date) => setFromDate(getDateWithStartOfDay(date)), []);

  const handleToDateChange = useCallback((date: Date) => setToDate(getDateWithEndOfDay(date)), []);

  const handleFromTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setFromTime(event.target.value),
    []
  );

  const handleToTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setToTime(event.target.value),
    []
  );

  const handleSubmitFilters = () => {
    const isSubmitDisabled = isFilterSubmitDisabled({
      fromValue,
      toValue,
      fromDate,
      toDate,
      activeColumn,
      selectedValues,
      checkedFilteredData,
      filterType: activeColumn ? filters[activeColumn].filterType : undefined,
      fromTime,
      toTime
    });

    if (isSubmitDisabled) {
      return;
    }

    handleSubmit({
      fromValue: fromValue.length ? Number(fromValue) : undefined,
      toValue: toValue.length ? Number(toValue) : undefined,
      fromDate,
      toDate,
      fromTime,
      toTime,
      selectedValues: selectedValues || undefined,
      searchQuery: searchQuery || undefined,
      currentDataSelectOptions: filteredDisplayedData,
      checkedFilteredData
    });
    handleClose();
  };

  if (!activeColumn) {
    return null;
  }

  return (
    <CustomPopover
      open={Boolean(anchorElement)}
      anchorEl={anchorElement}
      onClose={handleClose}
      id="Data filter"
      offset={{ x: 0, y: 20 }}
      style={{ maxHeight: 450 }}
    >
      <div className={styles.filterContainer}>
        <SortLabel
          sort={sort}
          activeColumn={activeColumn!}
          sortByColumn={sortByColumn}
          handleSortClick={handleSortClick}
          filterType={activeColumn ? filters[activeColumn].filterType : undefined}
        />
        {filters[activeColumn!].filterType === FILTER_TYPE.NUMBER ? (
          <NumericalFilterContent
            fromValue={fromValue}
            toValue={toValue}
            handleFromValueChange={handleFromValueChange}
            handleToValueChange={handleToValueChange}
            handleClearColumnFilter={handleClearColumnFilter}
            handleSubmit={handleSubmitFilters}
            clearEnabled={!!isFilterClearEnabled(filters, sortByColumn, activeColumn)}
          />
        ) : filters[activeColumn!].filterType === FILTER_TYPE.SELECT ? (
          <SelectFilterContent
            options={options!}
            value={selectedValues}
            clearEnabled={!!isFilterClearEnabled(filters, sortByColumn, activeColumn)}
            handleClearColumnFilter={handleClearColumnFilter}
            handleChange={handleSelectValuesChange}
          />
        ) : filters[activeColumn!].filterType === FILTER_TYPE.DATE ? (
          <DateFilterContent
            fromDate={fromDate}
            toDate={toDate}
            handleFromDateChange={handleFromDateChange}
            handleToDateChange={handleToDateChange}
            handleClearColumnFilter={handleClearColumnFilter}
            handleSubmit={handleSubmitFilters}
            clearEnabled={!!isFilterClearEnabled(filters, sortByColumn, activeColumn)}
          />
        ) : filters[activeColumn!].filterType === FILTER_TYPE.TIME ? (
          <TimeFilterContent
            fromTime={fromTime}
            toTime={toTime}
            handleFromTimeChange={handleFromTimeChange}
            handleToTimeChange={handleToTimeChange}
            handleClearColumnFilter={handleClearColumnFilter}
            handleSubmit={handleSubmitFilters}
            clearEnabled={!!isFilterClearEnabled(filters, sortByColumn, activeColumn)}
          />
        ) : (
          <TextFilterContent
            handleSearchQueryChange={handleSearchQueryChange}
            searchQuery={searchQuery}
            filteredDisplayedData={filteredDisplayedData}
            checkedFilteredData={checkedFilteredData}
            handleDataCheckChange={handleDataCheckChange}
            activeColumn={activeColumn}
            handleSelectAll={handleSelectAll}
            handleUnSelectAll={handleUnSelectAll}
            handleClearColumnFilter={handleClearColumnFilter}
            clearEnabled={!!isFilterClearEnabled(filters, sortByColumn, activeColumn)}
            minQueryLength={minQueryLength}
          />
        )}
        <FilterActions
          handleSubmit={handleSubmitFilters}
          handleClose={handleClose}
          fromValue={fromValue}
          toValue={toValue}
          fromDate={fromDate}
          toDate={toDate}
          fromTime={fromTime}
          toTime={toTime}
          activeColumn={activeColumn}
          filterType={activeColumn ? filters[activeColumn].filterType : undefined}
          selectedValues={selectedValues}
          checkedFilteredData={checkedFilteredData}
        />
      </div>
    </CustomPopover>
  );
};
